import React from 'react';
import { Activity, GitCommit, ShieldCheck, Target } from 'lucide-react';

/**
 * Commitment Control proof block.
 *
 * Four glass metric tiles render scope-drift KPIs after the delivery promise,
 * followed by a Stayed / Added / Removed segmented strip that makes the
 * baseline-vs-now story instantly readable. Pure HTML/SVG; no images.
 *
 * Numbers mirror the real Commitment Tracker product moment so the section is
 * product-true: Reliability 28.4%, Stability 51.7%, Net delta -6 (+9 / -15),
 * Delivery progress 11 / 27.
 */

type Tile = {
  id: string;
  label: string;
  value: string;
  delta?: string;
  tone: 'good' | 'neutral' | 'warn';
  hint: string;
  Icon: typeof Target;
};

const TILES: readonly Tile[] = [
  {
    id: 'reliability',
    label: 'Commitment Reliability',
    value: '28.4%',
    delta: 'Below target',
    tone: 'warn',
    hint: 'Baseline issues delivered with a done status.',
    Icon: Target,
  },
  {
    id: 'stability',
    label: 'Scope Stability',
    value: '51.7%',
    delta: 'Drifted after baseline',
    tone: 'warn',
    hint: 'How much of baseline scope stayed unchanged.',
    Icon: ShieldCheck,
  },
  {
    id: 'delta',
    label: 'Net Scope Delta',
    value: '-6',
    delta: '+9 added · −15 removed',
    tone: 'warn',
    hint: 'Net additions minus removals after baseline.',
    Icon: GitCommit,
  },
  {
    id: 'progress',
    label: 'Delivery Progress',
    value: '11 / 27',
    delta: '41% complete',
    tone: 'neutral',
    hint: 'Baseline issues delivered so far.',
    Icon: Activity,
  },
] as const;

type DriftSegment = {
  id: string;
  label: string;
  count: number;
  display: string;
  tone: 'stayed' | 'added' | 'removed';
};

/**
 * Stayed / Added / Removed counts derived from the tile data above:
 *   baseline = 33  -> 18 stayed + 15 removed
 *   added after baseline = 9
 *   current scope = 33 - 15 + 9 = 27 (matches Delivery Progress denominator)
 */
const DRIFT_SEGMENTS: readonly DriftSegment[] = [
  { id: 'stayed', label: 'Stayed', count: 18, display: '18', tone: 'stayed' },
  { id: 'added', label: 'Added', count: 9, display: '+9', tone: 'added' },
  { id: 'removed', label: 'Removed', count: 15, display: '−15', tone: 'removed' },
] as const;

function ProgressGlyph({ pct, tone }: { pct: number; tone: Tile['tone'] }): React.ReactElement {
  const dashed = Math.max(0, Math.min(100, pct));
  return (
    <svg
      className={`commitment-tile__glyph commitment-tile__glyph--${tone}`}
      viewBox="0 0 100 6"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="2" width="100" height="2" rx="1" fill="rgba(148, 163, 184, 0.18)" />
      <rect x="0" y="2" width={dashed} height="2" rx="1" className="commitment-tile__glyph-fill" />
    </svg>
  );
}

export function CommitmentMetricTiles(): React.ReactElement {
  const totalDrift = DRIFT_SEGMENTS.reduce((s, seg) => s + seg.count, 0);
  return (
    <div className="commitment-tiles-wrap">
      <ul className="commitment-tiles" aria-label="Commitment metrics">
        {TILES.map((tile) => {
          const pct =
            tile.id === 'reliability'
              ? 28
              : tile.id === 'stability'
                ? 52
                : tile.id === 'progress'
                  ? 41
                  : 38;
          return (
            <li key={tile.id} className={`commitment-tile commitment-tile--${tile.tone}`}>
              <header className="commitment-tile__head">
                <span className="commitment-tile__icon" aria-hidden="true">
                  <tile.Icon size={18} strokeWidth={1.6} />
                </span>
                <span className="commitment-tile__label">{tile.label}</span>
              </header>
              <div className="commitment-tile__value-row">
                <span className="commitment-tile__value">{tile.value}</span>
                {tile.delta ? <span className="commitment-tile__delta">{tile.delta}</span> : null}
              </div>
              <ProgressGlyph pct={pct} tone={tile.tone} />
              <p className="commitment-tile__hint">{tile.hint}</p>
            </li>
          );
        })}
      </ul>

      <section
        className="commitment-tiles__drift"
        aria-label="Scope change overview: stayed, added, removed"
      >
        <header className="commitment-tiles__drift-head">
          <span className="commitment-tiles__drift-eyebrow">Scope change overview</span>
          <span className="commitment-tiles__drift-meta">
            Recent change · 2 days after baseline
          </span>
        </header>
        <div className="commitment-tiles__drift-bar" role="presentation">
          {DRIFT_SEGMENTS.map((seg) => {
            const pct = (seg.count / totalDrift) * 100;
            return (
              <span
                key={seg.id}
                className={`commitment-tiles__drift-seg commitment-tiles__drift-seg--${seg.tone}`}
                style={{ flexBasis: `${pct}%` }}
                aria-hidden="true"
              />
            );
          })}
        </div>
        <ul className="commitment-tiles__drift-chips" aria-label="Scope change counts">
          {DRIFT_SEGMENTS.map((seg) => (
            <li
              key={seg.id}
              className={`commitment-tiles__drift-chip commitment-tiles__drift-chip--${seg.tone}`}
            >
              <span
                className={`commitment-tiles__drift-dot commitment-tiles__drift-dot--${seg.tone}`}
                aria-hidden="true"
              />
              <span className="commitment-tiles__drift-chip-label">{seg.label}</span>
              <span className="commitment-tiles__drift-chip-value">{seg.display}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
